import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getImageUrl } from '../utils/imageUrl';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogOut, LayoutDashboard, Archive, ChevronDown, ChevronRight, Globe, ImageIcon, Bell, ExternalLink, Truck, Package, AlertTriangle, Mail, Phone } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { translateStatus, translateUrgency } from '../i18n/translations';
import { hasExpress } from '../utils/express';
import { StatusFlowBar } from '../components/StatusFlowBar';
import { toast } from 'sonner';
import { TrackingDialog } from '../components/TrackingDialog';
import { DeliveredBadge } from '../components/DeliveredBadge';

const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const LANG_LABELS = { fr: "Français", en: "English", vi: "Tiếng Việt" };

const URGENCY_COLORS = {
  urgent: 'bg-red-100 text-red-800 border-red-200',
  prioritaire: 'bg-orange-100 text-orange-800 border-orange-200',
  classique: 'bg-slate-100 text-slate-700 border-slate-200',
  'non urgent': 'bg-blue-100 text-blue-700 border-blue-200',
};

function SuppliersView() {
  const { t } = useLanguage();
  const [suppliers, setSuppliers] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/suppliers`, { withCredentials: true })
      .then(r => setSuppliers(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Package size={20} /> {t('sup_title')} <Badge className="bg-slate-200 text-slate-700">{suppliers.length}</Badge></h2>
      {suppliers.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">{t('sup_empty')}</p>
      ) : suppliers.map(s => (
        <Card key={s.id} className="border-slate-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 font-bold flex-shrink-0">{(s.company_name || '?')[0].toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm text-slate-900">{s.company_name}</h3>
                <p className="text-xs text-slate-500">{s.firstname} {s.lastname}</p>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                {s.email && <span className="flex items-center gap-1"><Mail size={12} /> {s.email}</span>}
                {s.phone && <span className="flex items-center gap-1"><Phone size={12} /> {s.phone}</span>}
              </div>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}><Package size={14} className="mr-1" /> {t('sup_products')}</Button>
            </div>
            {expandedId === s.id && s.products?.length > 0 && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <Table><TableHeader><TableRow><TableHead className="font-medium">{t('sup_product_name')}</TableHead><TableHead className="font-medium text-right">{t('sup_product_price')}</TableHead></TableRow></TableHeader>
                  <TableBody>{s.products.map((p, i) => (<TableRow key={i}><TableCell className="text-sm font-medium">{p.name}</TableCell><TableCell className="text-sm text-right font-bold">{p.price ? Number(p.price).toLocaleString() : '—'}</TableCell></TableRow>))}</TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export const ManagerDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingLink, setTrackingLink] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingDetailOpen, setTrackingDetailOpen] = useState(false);
  const [trackingDetailNumber, setTrackingDetailNumber] = useState('');
  const [monthlyPieces, setMonthlyPieces] = useState([]);
  const [notifBanner, setNotifBanner] = useState(null);
  const [alertNotifs, setAlertNotifs] = useState([]);
  const prevOrderCount = React.useRef(null);
  const prevAlertKeys = React.useRef(null);
  const prevOrderStatuses = React.useRef(null);

  // My Info popup
  const [myInfo, setMyInfo] = useState(null);
  const [myInfoOpen, setMyInfoOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [prodStats, setProdStats] = useState(null);
  const [lateDialogOpen, setLateDialogOpen] = useState(false);

  useEffect(() => {
    fetchData(false);
    fetchMyInfo();
    fetchMonthlyPieces();
    const interval = setInterval(() => fetchData(true), 300000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const fetchMyInfo = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/my-info`, { withCredentials: true });
      setMyInfo(res.data);
    } catch (e) { console.error('Error fetching manager info:', e); }
  };

  const fetchMonthlyPieces = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/manager/monthly-pieces`, { withCredentials: true });
      setMonthlyPieces(res.data);
    } catch (e) { console.error('Error fetching monthly pieces:', e); }
  };

  const handleChangePassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error(t('confirm_password'));
      return;
    }
    if (pwdForm.new_password.length < 4) {
      toast.error('Mot de passe trop court');
      return;
    }
    setPwdLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/partner/change-password`, {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password
      }, { withCredentials: true });
      toast.success(t('password_changed'));
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowChangePassword(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('password_error'));
    } finally {
      setPwdLoading(false);
    }
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === 'granted') {
      toast.success(t('notifications_enabled'));
      new Notification('Panther', { body: t('notifications_enabled'), icon: LOGO_URL });
    } else {
      toast.error(t('notifications_denied'));
    }
  };

  const ALL_STATUSES = ['commandée', 'impression', 'couture', 'livraison bureau', 'en cours de livraison client', 'livrée'];

  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        gain2.gain.value = 0.3;
        osc2.start();
        osc2.stop(ctx.currentTime + 0.3);
      }, 200);
    } catch (e) { console.error('Audio playback not available:', e); }
  }, []);

  const handleManagerStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/manager/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`${t('status')}: ${translateStatus(newStatus, t)}`);
      fetchData(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const fetchData = async (isPolling = false) => {
    try {
      const [ordersRes, archivesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/manager/orders`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/manager/archives`, { withCredentials: true })
      ]);
      // Fetch late orders
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/stats`, { withCredentials: true })
        .then(r => setProdStats(r.data)).catch(() => {});
      const newOrders = ordersRes.data;

      if (isPolling) {
        let newOrderDetected = false;
        let statusChangeDetected = false;
        let notifMsg = '';

        // Detect new orders
        if (prevOrderCount.current !== null && newOrders.length > prevOrderCount.current) {
          const diff = newOrders.length - prevOrderCount.current;
          notifMsg = diff === 1 ? t('new_order_notification') : `${diff} ${t('new_orders_notification')}`;
          newOrderDetected = true;
        }

        // Detect status changes
        if (prevOrderStatuses.current) {
          for (const o of newOrders) {
            const key = o.order_id || o.id;
            const prevStatus = prevOrderStatuses.current[key];
            if (prevStatus && prevStatus !== o.status) {
              notifMsg = t('status_update_notification');
              statusChangeDetected = true;
              break;
            }
          }
        }

        if (newOrderDetected) {
          setNotifBanner(notifMsg);
          playNotifSound();
          setTimeout(() => setNotifBanner(null), 8000);
        } else if (statusChangeDetected) {
          setNotifBanner(notifMsg);
          playNotifSound();
          setTimeout(() => setNotifBanner(null), 8000);
        }

        // Detect color alerts
        const currentColorKeys = new Set(newOrders.filter(o => o.color === 'orange' || o.color === 'red').map(o => `${o.order_id}-${o.color}`));
        const prevKeys = prevAlertKeys.current || new Set();
        const newColorAlerts = [...currentColorKeys].filter(k => !prevKeys.has(k));
        if (newColorAlerts.length > 0) {
          const hasRed = newColorAlerts.some(k => k.endsWith('-red'));
          const msg = hasRed ? t('alert_red_notification') : t('alert_orange_notification');
          setAlertNotifs(prev => [...prev, { id: Date.now(), msg }]);
          playNotifSound();
          setTimeout(() => setAlertNotifs([]), 8000);
        }
        prevAlertKeys.current = currentColorKeys;
      } else {
        prevAlertKeys.current = new Set(newOrders.filter(o => o.color === 'orange' || o.color === 'red').map(o => `${o.order_id}-${o.color}`));
      }

      prevOrderCount.current = newOrders.length;
      // Track statuses for change detection
      const statusMap = {};
      newOrders.forEach(o => { statusMap[o.order_id || o.id] = o.status; });
      prevOrderStatuses.current = statusMap;

      setOrders(newOrders);
      setArchives(archivesRes.data);
    } catch (error) {
      console.error('Error fetching manager data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/language`, { language: lang }, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getRowBgColor = (color) => {
    switch (color) {
      case 'green': return 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100';
      case 'orange': return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
      case 'red': return 'bg-red-50 border-red-200 hover:bg-red-100';
      case 'red_blink': return 'bg-red-50 border-red-300 hover:bg-red-100 animate-pulse';
      default: return 'bg-slate-50 border-slate-200 hover:bg-slate-100';
    }
  };

  const [detailOrder, setDetailOrder] = useState(null);
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [quantityOrder, setQuantityOrder] = useState(null);

  const groupByMonth = (list) => {
    const groups = {};
    for (const o of list) {
      const label = o.month_label || 'Inconnu';
      if (!groups[label]) groups[label] = [];
      groups[label].push(o);
    }
    return Object.entries(groups).map(([label, items]) => ({ label, orders: items }));
  };

  const toggleMonth = (label) => {
    setExpandedMonths(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const openImagePopup = (images) => {
    setImagePopupImages(images);
    setImagePopupOpen(true);
  };

  const openTrackingDialog = (order) => {
    setTrackingOrder(order);
    setTrackingLink(order.tracking_link || '');
    setTrackingNumber(order.tracking_number || '');
    setTrackingDialogOpen(true);
  };

  const handleSaveTracking = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${trackingOrder.order_id}/tracking`, {
        tracking_link: trackingLink,
        tracking_number: trackingNumber
      }, { withCredentials: true });
      setTrackingDialogOpen(false);
      fetchData();
    } catch (e) { console.error('Error saving tracking:', e); toast.error('Erreur'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col fixed inset-y-0">
          <div className="p-6 border-b border-slate-200">
            <img src={LOGO_URL} alt="Panther" className="h-10 object-contain" data-testid="sidebar-logo" />
            <p className="text-xs text-slate-600 mt-2">{user?.name}</p>
            <p className="text-xs text-slate-500">Manager</p>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            <button
              data-testid="manager-nav-orders"
              onClick={() => setActiveTab('orders')}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors ${activeTab === 'orders' ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <LayoutDashboard size={18} />
              {t('orders_in_progress')}
            </button>
            <button
              data-testid="manager-nav-archives"
              onClick={() => setActiveTab('archives')}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors ${activeTab === 'archives' ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Archive size={18} />
              {t('archives')}
            </button>
            <button
              data-testid="manager-nav-suppliers"
              onClick={() => setActiveTab('suppliers')}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors ${activeTab === 'suppliers' ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Package size={18} />
              {t('sup_title')}
            </button>
          </nav>
          <div className="p-4 border-t border-slate-200 space-y-2">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-slate-500" />
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger data-testid="manager-lang-switcher" className="h-8 text-xs border-slate-200 flex-1">
                  <SelectValue>{LANG_LABELS[language]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="vi">Tiếng Việt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="manager-my-info-button" variant="outline" onClick={() => setMyInfoOpen(true)} className="w-full justify-start text-slate-600 text-sm">
              {t('my_information')}
            </Button>
            <Button data-testid="manager-logout" onClick={handleLogout} variant="ghost" className="w-full justify-start text-slate-600">
              <LogOut size={18} className="mr-3" />
              {t('logout')}
            </Button>
          </div>
        </aside>

        <main className="flex-1 md:ml-64 p-6 md:p-8">
          {/* Notification banner */}
          {notifBanner && (
            <div data-testid="new-order-notification" className="mb-4 animate-in slide-in-from-top">
              <div className="bg-red-600 text-white rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
                <Bell size={20} className="animate-bounce" />
                <span className="font-semibold">{notifBanner}</span>
                <button onClick={() => setNotifBanner(null)} className="ml-auto text-red-200 hover:text-white text-lg font-bold">&times;</button>
              </div>
            </div>
          )}
          {alertNotifs.length > 0 && (
            <div data-testid="color-alert-notification" className="mb-4 space-y-2">
              {alertNotifs.map(n => (
                <div key={n.id} className={`rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg ${n.msg.includes('red') || n.msg.includes('retard') || n.msg.includes('Urgent') || n.msg.includes('quá hạn') ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                  <Bell size={20} className="animate-bounce" />
                  <span className="font-semibold">{n.msg}</span>
                  <button onClick={() => setAlertNotifs(prev => prev.filter(x => x.id !== n.id))} className="ml-auto opacity-70 hover:opacity-100 text-lg font-bold">&times;</button>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="space-y-6">
              {/* Late orders banner */}
              {prodStats?.late_this_month_count > 0 && (
                <Card className="border-red-300 bg-red-50">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <AlertTriangle size={28} className="text-red-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-red-800">{t('prod_late_title')} <span className="text-2xl font-black">{prodStats.late_this_month_count}</span> {t('prod_late_suffix')}</p>
                        <p className="text-xs text-red-600 mt-0.5">RETARD IMPRIMEUR</p>
                      </div>
                    </div>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs gap-1" onClick={() => setLateDialogOpen(true)}>
                      <AlertTriangle size={12} /> {t('prod_late_see')}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Late orders dialog */}
              <Dialog open={lateDialogOpen} onOpenChange={setLateDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle size={20} /> {t('prod_late_see')}</DialogTitle>
                  </DialogHeader>
                  {prodStats?.late_this_month?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('prod_late_order')}</TableHead>
                          <TableHead>{t('prod_late_deadline')}</TableHead>
                          <TableHead>{t('prod_late_changed')}</TableHead>
                          <TableHead>{t('prod_late_days')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {prodStats.late_this_month.map(lo => (
                          <TableRow key={lo.id} className="bg-red-50">
                            <TableCell>
                              <p className="font-bold text-sm">{lo.order_number}</p>
                              <p className="text-xs text-slate-500">{lo.client_name}</p>
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-red-700">{lo.couture_deadline ? new Date(lo.couture_deadline).toLocaleDateString('fr-FR') : '—'}</TableCell>
                            <TableCell className="text-sm">{lo.status_changed_at ? new Date(lo.status_changed_at).toLocaleDateString('fr-FR') : <span className="text-red-600 font-semibold">{t('prod_late_still')}</span>}</TableCell>
                            <TableCell><Badge className="bg-red-100 text-red-800">{lo.days_late} {t('prod_late_days')}</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">Aucune commande en retard ce mois-ci</p>
                  )}
                </DialogContent>
              </Dialog>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('manager_title')}</h1>
                  <p className="text-sm text-slate-600 mt-1">{t('manager_subtitle')}</p>
                  <StatusFlowBar />
                </div>
                <div className="flex items-center gap-2">
                  {notifPermission !== 'granted' ? (
                    <Button data-testid="manager-enable-notifications" variant="outline" size="sm" onClick={requestNotifPermission} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                      <Bell size={14} className="mr-1" /> {t('enable_notifications')}
                    </Button>
                  ) : (
                    <Badge data-testid="manager-notifications-enabled" className="bg-emerald-100 text-emerald-700 border border-emerald-200 h-8 px-3">
                      <Bell size={14} className="mr-1" /> {t('notifications_enabled')}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Graphique pièces vendues par mois */}
              {monthlyPieces.length > 0 && (
                <Card data-testid="manager-chart-pieces" className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">
                      {t('pieces_sold_monthly')} {new Date().getFullYear()}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={monthlyPieces}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                        <Tooltip
                          contentStyle={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                          formatter={(value) => [`${value.toLocaleString('fr-FR')}`, '']}
                        />
                        <Bar dataKey="pieces" name={t('pieces_sold')} fill="#7c3aed" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              <Card data-testid="manager-orders-card" className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">{t('orders_in_progress')} ({orders.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <p className="text-sm text-slate-500">{t('no_orders_in_progress')}</p>
                  ) : (
                    <div className="space-y-6">
                      {/* Group 1: Orders at livraison bureau or en cours de livraison client */}
                      {(() => {
                        const advancedStatuses = ['livraison bureau', 'en cours de livraison client'];
                        const advancedOrders = orders.filter(o => advancedStatuses.includes(o.status));
                        const earlyOrders = orders.filter(o => !advancedStatuses.includes(o.status));
                        return (
                          <>
                            {advancedOrders.length > 0 && (
                              <div>
                                <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider mb-2">{translateStatus('livraison bureau', t)} / {translateStatus('en cours de livraison client', t)} ({advancedOrders.length})</h3>
                                <div className="space-y-2">
                                  {advancedOrders.map((order) => (
                                    <div
                                      key={order.order_id}
                                      data-testid={`manager-alert-${order.color}`}
                                      className={`flex items-center p-3 rounded-md border transition-colors cursor-pointer ${getRowBgColor(order.color)}`}
                                      onClick={() => setDetailOrder(detailOrder?.order_id === order.order_id ? null : order)}
                                    >
                                      <div className="mr-3 flex-shrink-0">
                                        {order.images && order.images.length > 0 ? (
                                          <img src={getImageUrl(order.images[0])} alt="" data-testid="order-thumbnail" className="w-12 h-12 object-cover rounded border border-slate-300 cursor-pointer hover:opacity-80 transition-opacity" onClick={(e) => { e.stopPropagation(); openImagePopup(order.images); }} />
                                        ) : (
                                          <div className="w-12 h-12 bg-slate-200 rounded border border-slate-300 flex items-center justify-center"><ImageIcon size={16} className="text-slate-400" /></div>
                                        )}
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900">
                                          {order.order_number} — {order.client_company} — {order.client_name} {order.client_firstname}
                                          {hasExpress(order) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                                          {order.urgency && <span data-testid="urgency-badge" className={`ml-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase ${URGENCY_COLORS[order.urgency] || URGENCY_COLORS.classique}`}>{translateUrgency(order.urgency, t)}</span>}
                                        </p>
                                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                                          <span className="text-xs text-slate-500">{order.order_date ? new Date(order.order_date).toLocaleDateString('fr-FR') : ''}</span>
                                          <Badge className={`text-xs border uppercase ${
                                            order.status === 'livraison bureau' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                            'bg-orange-100 text-orange-800 border-orange-200'
                                          }`}>{translateStatus(order.status, t)}</Badge>
                                          <span className="text-xs text-slate-600">{order.client_country}</span>
                                          {order.alert && (
                                            <span data-testid="order-alert-blink" className="alert-blink bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded uppercase">ALERT</span>
                                          )}
                                          {order.tissu && <span className="font-bold text-emerald-600 text-xs">{order.tissu}</span>}
                                          <Button size="sm" variant="outline" className="h-5 text-xs px-2" onClick={(e) => { e.stopPropagation(); setQuantityOrder(order); setQuantityDialogOpen(true); }}>{t('quantities')}</Button>
                                          {order.status === 'en cours de livraison client' && (
                                            <>
                                              {!order.tracking_link && (
                                                <Button data-testid="add-tracking-btn" size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={(e) => { e.stopPropagation(); openTrackingDialog(order); }}><Truck size={12} /> {t('add_tracking')}</Button>
                                              )}
                                              {order.tracking_number && <span className="text-xs font-medium text-slate-700">{t('package')}: {order.tracking_number}</span>}
                                              {order.tracking_number && (
                                                <Button data-testid="tracking-detail-btn" size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); setTrackingDetailNumber(order.tracking_number); setTrackingDetailOpen(true); }}>
                                                  <Package size={12} /> SUIVI
                                                </Button>
                                              )}
                                              <DeliveredBadge date={order.tracking_delivered_date} />
                                              {order.tracking_link && <Button size="sm" variant="ghost" className="h-5 text-xs text-slate-500 px-1" onClick={(e) => { e.stopPropagation(); openTrackingDialog(order); }}>{t('edit')}</Button>}
                                            </>
                                          )}
                                          {order.dropbox_link && (
                                            <a href={order.dropbox_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase h-6">DROPBOX</Button>
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3 ml-4">
                                        <Select value={order.status} onValueChange={(v) => handleManagerStatusChange(order.order_id, v)}>
                                          <SelectTrigger data-testid={`manager-status-select-${order.order_id}`} className="w-44 h-8 text-xs"><SelectValue>{translateStatus(order.status, t)}</SelectValue></SelectTrigger>
                                          <SelectContent>
                                            {(order.allowed_statuses?.length > 0 ? order.allowed_statuses : ALL_STATUSES).map(s => (
                                              <SelectItem key={s} value={s}>{translateStatus(s, t)}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        {order.status === 'en cours de livraison client' ? (
                                          <div className="text-right px-3 py-1 rounded bg-emerald-200 text-emerald-900">
                                            <p className="text-xs font-medium uppercase">{t('delivery_client')}</p>
                                            <p className="text-sm font-bold">{order.client_delivery_date ? new Date(order.client_delivery_date).toLocaleDateString('fr-FR') : '—'}</p>
                                          </div>
                                        ) : order.bureau_delivery_date && (
                                          <div className={`text-right px-3 py-1 rounded ${
                                            order.color === 'green' ? 'bg-emerald-200 text-emerald-900' :
                                            order.color === 'orange' ? 'bg-amber-200 text-amber-900' :
                                            'bg-red-200 text-red-900'
                                          }`}>
                                            <p className="text-xs font-medium uppercase">{t('delivery_office')}</p>
                                            <p className="text-sm font-bold">{new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR')}</p>
                                            {order.color === 'red_blink' && order.client_delivery_date && (
                                              <p className="text-xs font-bold text-red-700 mt-1">Client : {new Date(order.client_delivery_date).toLocaleDateString('fr-FR')}</p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      {detailOrder?.order_id === order.order_id && (
                                        <div className="mt-2 p-3 bg-white rounded border border-slate-200 text-sm space-y-1" data-testid="order-detail-panel" onClick={(e) => e.stopPropagation()}>
                                          <div className="grid grid-cols-4 gap-4">
                                            <div><span className="text-slate-500">{t('piece_type')} :</span> <span className="font-medium">{order.piece_type || '—'}</span></div>
                                            <div><span className="text-slate-500">Tissu :</span> <span className="font-bold text-emerald-600">{order.tissu || '—'}</span></div>
                                            <div><span className="text-slate-500">{t('quantity')} :</span> <span className="font-medium">{order.num_pieces || 0}</span></div>
                                            <div><span className="text-slate-500">{t('estimated_weight')} :</span> <span className="font-medium">{order.package_weight_kg || 0} kg</span></div>
                                          </div>
                                          {order.order_lines && order.order_lines.length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-slate-100">
                                              <p className="text-xs text-slate-500 mb-1 font-medium">{t('order_lines')} :</p>
                                              {order.order_lines.map((line, li) => (
                                                <div key={li} className="text-xs text-slate-700 flex gap-4">
                                                  <span>{line.product_name}</span>
                                                  {line.tissu && <span className="text-purple-600 font-medium">{line.tissu}</span>}
                                                  <span>x{line.quantity}</span>
                                                  <span>{line.weight_kg} kg</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {earlyOrders.length > 0 && (
                              <div>
                                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">{translateStatus('commandée', t)} / {translateStatus('impression', t)} / {translateStatus('couture', t)} ({earlyOrders.length})</h3>
                                <div className="space-y-2">
                                  {earlyOrders.map((order) => (
                                    <div
                                      key={order.order_id}
                                      data-testid={`manager-alert-${order.color}`}
                                      className={`flex items-center p-3 rounded-md border transition-colors cursor-pointer ${getRowBgColor(order.color)}`}
                                      onClick={() => setDetailOrder(detailOrder?.order_id === order.order_id ? null : order)}
                                    >
                                      <div className="mr-3 flex-shrink-0">
                                        {order.images && order.images.length > 0 ? (
                                          <img src={getImageUrl(order.images[0])} alt="" data-testid="order-thumbnail" className="w-12 h-12 object-cover rounded border border-slate-300 cursor-pointer hover:opacity-80 transition-opacity" onClick={(e) => { e.stopPropagation(); openImagePopup(order.images); }} />
                                        ) : (
                                          <div className="w-12 h-12 bg-slate-200 rounded border border-slate-300 flex items-center justify-center"><ImageIcon size={16} className="text-slate-400" /></div>
                                        )}
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900">
                                          {order.order_number} — {order.client_company} — {order.client_name} {order.client_firstname}
                                          {hasExpress(order) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                                          {order.urgency && <span data-testid="urgency-badge" className={`ml-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase ${URGENCY_COLORS[order.urgency] || URGENCY_COLORS.classique}`}>{translateUrgency(order.urgency, t)}</span>}
                                        </p>
                                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                                          <span className="text-xs text-slate-500">{order.order_date ? new Date(order.order_date).toLocaleDateString('fr-FR') : ''}</span>
                                          <Badge className={`text-xs border uppercase ${
                                            order.status === 'commandée' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                            order.status === 'impression' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                                            'bg-indigo-100 text-indigo-800 border-indigo-200'
                                          }`}>{translateStatus(order.status, t)}</Badge>
                                          <span className="text-xs text-slate-600">{order.client_country}</span>
                                          {order.alert && (
                                            <span data-testid="order-alert-blink" className="alert-blink bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded uppercase">ALERT</span>
                                          )}
                                          {order.tissu && <span className="font-bold text-emerald-600 text-xs">{order.tissu}</span>}
                                          <Button size="sm" variant="outline" className="h-5 text-xs px-2" onClick={(e) => { e.stopPropagation(); setQuantityOrder(order); setQuantityDialogOpen(true); }}>{t('quantities')}</Button>
                                          {order.dropbox_link && (
                                            <a href={order.dropbox_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase h-6">DROPBOX</Button>
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3 ml-4">
                                        <Select value={order.status} onValueChange={(v) => handleManagerStatusChange(order.order_id, v)}>
                                          <SelectTrigger data-testid={`manager-status-select-${order.order_id}`} className="w-44 h-8 text-xs"><SelectValue>{translateStatus(order.status, t)}</SelectValue></SelectTrigger>
                                          <SelectContent>
                                            {(order.allowed_statuses?.length > 0 ? order.allowed_statuses : ALL_STATUSES).map(s => (
                                              <SelectItem key={s} value={s}>{translateStatus(s, t)}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        {order.bureau_delivery_date && (
                                          <div className={`text-right px-3 py-1 rounded ${
                                            order.color === 'green' ? 'bg-emerald-200 text-emerald-900' :
                                            order.color === 'orange' ? 'bg-amber-200 text-amber-900' :
                                            'bg-red-200 text-red-900'
                                          }`}>
                                            <p className="text-xs font-medium uppercase">{t('delivery_office')}</p>
                                            <p className="text-sm font-bold">{new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR')}</p>
                                            {order.color === 'red_blink' && order.client_delivery_date && (
                                              <p className="text-xs font-bold text-red-700 mt-1">Client : {new Date(order.client_delivery_date).toLocaleDateString('fr-FR')}</p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      {detailOrder?.order_id === order.order_id && (
                                        <div className="mt-2 p-3 bg-white rounded border border-slate-200 text-sm space-y-1" data-testid="order-detail-panel" onClick={(e) => e.stopPropagation()}>
                                          <div className="grid grid-cols-4 gap-4">
                                            <div><span className="text-slate-500">{t('piece_type')} :</span> <span className="font-medium">{order.piece_type || '—'}</span></div>
                                            <div><span className="text-slate-500">Tissu :</span> <span className="font-bold text-emerald-600">{order.tissu || '—'}</span></div>
                                            <div><span className="text-slate-500">{t('quantity')} :</span> <span className="font-medium">{order.num_pieces || 0}</span></div>
                                            <div><span className="text-slate-500">{t('estimated_weight')} :</span> <span className="font-medium">{order.package_weight_kg || 0} kg</span></div>
                                          </div>
                                          {order.order_lines && order.order_lines.length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-slate-100">
                                              <p className="text-xs text-slate-500 mb-1 font-medium">{t('order_lines')} :</p>
                                              {order.order_lines.map((line, li) => (
                                                <div key={li} className="text-xs text-slate-700 flex gap-4">
                                                  <span>{line.product_name}</span>
                                                  {line.tissu && <span className="text-purple-600 font-medium">{line.tissu}</span>}
                                                  <span>x{line.quantity}</span>
                                                  <span>{line.weight_kg} kg</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'archives' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('archives')}</h1>
                <p className="text-sm text-slate-600 mt-1">{archives.length} {t('delivered_orders')}</p>
              </div>
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="pt-6">
                  {archives.length === 0 ? (
                    <p className="text-center py-8 text-sm text-slate-500">{t('no_orders_in_progress')}</p>
                  ) : (
                    <div className="space-y-3">
                      {groupByMonth(archives).map((group) => (
                        <div key={group.label} data-testid="manager-archive-month" className="border border-slate-200 rounded-lg overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                            onClick={() => toggleMonth(group.label)}
                          >
                            <div className="flex items-center gap-3">
                              {expandedMonths[group.label] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                              <span className="font-semibold text-slate-900">{group.label}</span>
                            </div>
                            <Badge className="bg-slate-200 text-slate-700 border-0">{group.orders.length}</Badge>
                          </button>
                          {expandedMonths[group.label] && (
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-white">
                                  <TableHead className="font-medium w-14"></TableHead>
                                  <TableHead className="font-medium">{t('order_number')}</TableHead>
                                  <TableHead className="font-medium">{t('company')}</TableHead>
                                  <TableHead className="font-medium">{t('client')}</TableHead>
                                  <TableHead className="font-medium">{t('country')}</TableHead>
                                  <TableHead className="font-medium">{t('order_date')}</TableHead>
                                  <TableHead className="font-medium">{t('status')}</TableHead>
                                  <TableHead className="font-medium">{t('detail')}</TableHead>
                                  <TableHead className="font-medium">DROPBOX</TableHead>
                                  <TableHead className="font-medium">Suivi</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.orders.map((order) => (
                                  <TableRow key={order.order_id}>
                                    <TableCell>
                                      {order.images?.length > 0 ? (
                                        <img
                                          src={getImageUrl(order.images[0])}
                                          alt=""
                                          className="w-10 h-10 object-cover rounded cursor-pointer"
                                          onClick={() => openImagePopup(order.images)}
                                        />
                                      ) : (
                                        <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                                          <ImageIcon size={14} className="text-slate-400" />
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {order.order_number}
                                      {hasExpress(order) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                                      {order.urgency && <span data-testid="urgency-badge" className={`ml-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase ${URGENCY_COLORS[order.urgency] || URGENCY_COLORS.classique}`}>{translateUrgency(order.urgency, t)}</span>}
                                    </TableCell>
                                    <TableCell>{order.client_company}</TableCell>
                                    <TableCell>{order.client_name} {order.client_firstname}</TableCell>
                                    <TableCell>{order.client_country}</TableCell>
                                    <TableCell className="text-sm text-slate-700">{order.order_date ? new Date(order.order_date).toLocaleDateString('fr-FR') : '—'}</TableCell>
                                    <TableCell>
                                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs uppercase">{translateStatus(order.status, t)}</Badge>
                                    </TableCell>
                                    <TableCell className="text-xs text-slate-600">
                                      {order.piece_type || '—'} · {order.num_pieces || 0} · {order.package_weight_kg || 0} kg
                                    </TableCell>
                                    <TableCell>
                                      {order.dropbox_link ? (
                                        <a href={order.dropbox_link} target="_blank" rel="noreferrer">
                                          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase">DROPBOX</Button>
                                        </a>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                      {order.tracking_number && (
                                        <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); setTrackingDetailNumber(order.tracking_number); setTrackingDetailOpen(true); }}>
                                          <Package size={12} /> SUIVI
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Suppliers tab */}
          {activeTab === 'suppliers' && (
            <div className="space-y-6">
              <SuppliersView />
            </div>
          )}

        </main>
      </div>

      {/* MES INFORMATIONS Popup */}
      <Dialog open={myInfoOpen} onOpenChange={setMyInfoOpen}>
        <DialogContent className="max-w-md" data-testid="manager-my-info-dialog">
          <DialogHeader>
            <DialogTitle>{t('my_information')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <p data-testid="manager-my-info-email" className="text-sm font-medium">{myInfo?.email || user?.email}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">{t('password')}</Label>
                <p className="text-sm font-medium">********</p>
              </div>
            </div>

            {!showChangePassword ? (
              <Button data-testid="manager-change-password-btn" variant="outline" size="sm" onClick={() => setShowChangePassword(true)} className="w-full">
                {t('change_password')}
              </Button>
            ) : (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('current_password')}</Label>
                    <Input data-testid="manager-current-password" type="password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('new_password')}</Label>
                    <Input data-testid="manager-new-password" type="password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('confirm_password')}</Label>
                    <Input data-testid="manager-confirm-password" type="password" value={pwdForm.confirm_password} onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="manager-submit-password" size="sm" onClick={handleChangePassword} disabled={pwdLoading} className="flex-1 bg-slate-900 hover:bg-slate-800">
                    {pwdLoading ? '...' : t('save')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowChangePassword(false); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); }}>{t('cancel')}</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
        <DialogContent className="max-w-2xl" data-testid="image-popup-dialog">
          <DialogHeader>
            <DialogTitle>{t('image_popup_title')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {imagePopupImages.map((img, i) => (
              <img key={`popup-${img}`} src={getImageUrl(img)} alt="" className="w-full rounded-lg border border-slate-200" />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
        <DialogContent className="max-w-md" data-testid="tracking-dialog">
          <DialogHeader>
            <DialogTitle>Tracking — {trackingOrder?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">{t('tracking_link_label')}</Label>
              <Input data-testid="tracking-link-input" value={trackingLink} onChange={(e) => setTrackingLink(e.target.value)} placeholder="https://..." className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">{t('tracking_number_label')}</Label>
              <Input data-testid="tracking-number-input" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Ex: FR123456789" className="mt-1" />
            </div>
            <Button data-testid="save-tracking-btn" className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleSaveTracking}>{t('save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={quantityDialogOpen} onOpenChange={setQuantityDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="quantity-dialog">
          <DialogHeader>
            <DialogTitle>{t('quantities_detail')} — {quantityOrder?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {quantityOrder?.tissu && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{t('tissu')} :</span>
                <span className="font-bold text-emerald-600">{quantityOrder.tissu}</span>
              </div>
            )}
            {quantityOrder?.order_lines && quantityOrder.order_lines.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('product_name')}</TableHead>
                    <TableHead>{t('tissu')}</TableHead>
                    <TableHead className="text-right">{t('quantity')}</TableHead>
                    <TableHead className="text-right">{t('estimated_weight')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quantityOrder.order_lines.map((line, i) => (
                    <TableRow key={`${line.product_name}-${i}`}>
                      <TableCell className="font-medium">{line.product_name}</TableCell>
                      <TableCell className="font-bold text-emerald-600">{line.tissu || '—'}</TableCell>
                      <TableCell className="text-right">{line.quantity}</TableCell>
                      <TableCell className="text-right">{line.weight_kg} kg</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-sm text-slate-500">
                <p>{t('piece_type')} : {quantityOrder?.piece_type || '—'}</p>
                <p>{t('quantity')} : {quantityOrder?.num_pieces || 0}</p>
                <p>{t('estimated_weight')} : {quantityOrder?.package_weight_kg || 0} kg</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TrackingDialog
        open={trackingDetailOpen}
        onOpenChange={setTrackingDetailOpen}
        trackingNumber={trackingDetailNumber}
      />
    </div>
  );
};
